import { useEffect, useRef, useState } from 'react'
import moment from 'moment'

import UserPhoto from '../UserPhoto'

import './RecipeAudioPlayer.css'

const BAR_COUNT = 15
const MIN_BAR = 0.2

const formatLength = (seconds) => {
  if (!isFinite(seconds) || seconds <= 0) return '--:--'
  const minutes = Math.floor(seconds / 60)
  const rest = Math.round(seconds % 60)
  return `${String(minutes).padStart(2, '0')}:${String(rest).padStart(2, '0')}`
}

// Stand-in shape when the file can't be read (e.g. served from another address)
const fallbackBars = (seed = 1) =>
  Array.from({ length: BAR_COUNT }, (_, i) => {
    const wave = Math.abs(Math.sin((i + 1) * 1.7 + seed) * Math.cos(i * 0.6 + seed * 0.3))
    return MIN_BAR + wave * (1 - MIN_BAR)
  })

const readBars = async (src) => {
  const AudioContext = window.AudioContext || window.webkitAudioContext
  const response = await fetch(src)
  const buffer = await response.arrayBuffer()
  const context = new AudioContext()
  try {
    const audio = await new Promise((resolve, reject) =>
      context.decodeAudioData(buffer, resolve, reject)
    )
    const samples = audio.getChannelData(0)
    const size = Math.floor(samples.length / BAR_COUNT)
    const peaks = Array.from({ length: BAR_COUNT }, (_, i) => {
      let peak = 0
      for (let j = i * size; j < (i + 1) * size; j += 16) {
        peak = Math.max(peak, Math.abs(samples[j]))
      }
      return peak
    })
    const loudest = Math.max(...peaks) || 1
    return {
      bars: peaks.map((peak) => MIN_BAR + (peak / loudest) * (1 - MIN_BAR)),
      duration: audio.duration,
    }
  } finally {
    context.close?.()
  }
}

const PlayIcon = () => (
  <svg viewBox="0 0 24 24" width="44" height="44" aria-hidden="true">
    <path d="M7 4.5v15a1 1 0 0 0 1.5.86l12-7.5a1 1 0 0 0 0-1.72l-12-7.5A1 1 0 0 0 7 4.5z" />
  </svg>
)

const PauseIcon = () => (
  <svg viewBox="0 0 24 24" width="44" height="44" aria-hidden="true">
    <rect x="5" y="4" width="5" height="16" rx="1.5" />
    <rect x="14" y="4" width="5" height="16" rx="1.5" />
  </svg>
)

const RecipeAudioPlayer = ({ src, recordedAt, author = {}, seed }) => {
  const audioRef = useRef(null)
  const [isPlaying, setIsPlaying] = useState(false)
  const [progress, setProgress] = useState(0)
  const [duration, setDuration] = useState(0)
  const [bars, setBars] = useState(() => fallbackBars(seed))

  useEffect(() => {
    let active = true
    readBars(src)
      .then((result) => {
        if (!active) return
        setBars(result.bars)
        setDuration((current) => (isFinite(current) && current > 0 ? current : result.duration))
      })
      .catch(() => {})
    return () => {
      active = false
    }
  }, [src])

  const togglePlay = () => {
    const audio = audioRef.current
    if (!audio) return
    audio.paused ? audio.play().catch(() => setIsPlaying(false)) : audio.pause()
  }

  const seek = (e) => {
    const audio = audioRef.current
    if (!audio || !isFinite(duration) || duration <= 0) return
    const { left, width } = e.currentTarget.getBoundingClientRect()
    const ratio = Math.min(Math.max((e.clientX - left) / width, 0), 1)
    audio.currentTime = ratio * duration
    setProgress(ratio)
  }

  const onLoadedMetadata = (e) => {
    const { duration: length } = e.currentTarget
    if (isFinite(length) && length > 0) setDuration(length)
  }

  const onTimeUpdate = (e) => {
    const { currentTime } = e.currentTarget
    setProgress(duration > 0 ? currentTime / duration : 0)
  }

  const onEnded = () => {
    setIsPlaying(false)
    setProgress(0)
  }

  const playedBars = Math.round(progress * BAR_COUNT)

  return (
    <div className="recipeAudioPlayer">
      <audio
        ref={audioRef}
        src={src}
        preload="metadata"
        onPlay={() => setIsPlaying(true)}
        onPause={() => setIsPlaying(false)}
        onEnded={onEnded}
        onLoadedMetadata={onLoadedMetadata}
        onTimeUpdate={onTimeUpdate}
      />

      <div className="recipeAudioPlay">
        <button
          type="button"
          className="recipeAudioPlayButton"
          onClick={togglePlay}
          aria-label={isPlaying ? 'Pause the family memory' : 'Play the family memory'}
        >
          {isPlaying ? <PauseIcon /> : <PlayIcon />}
        </button>
        <p className="recipeAudioCaption">
          Just tap <span className="recipeAudioCaptionBold">{isPlaying ? 'Pause' : 'Play'}</span>
        </p>
      </div>

      <div className="recipeAudioMiddle">
        {recordedAt && (
          <p className="recipeAudioRecorded">
            Recorded on:{' '}
            <span className="recipeAudioRecordedDate">
              {moment(recordedAt).format('MMM D, YYYY')}
            </span>
          </p>
        )}
        <div className="recipeAudioWave" onClick={seek} aria-hidden="true">
          {bars.map((height, index) => (
            <span
              key={index}
              className={`recipeAudioBar ${index < playedBars ? 'recipeAudioBarPlayed' : ''}`}
              style={{ height: `${Math.round(height * 100)}%` }}
            />
          ))}
        </div>
        <p className="recipeAudioLength">Length {formatLength(duration)} minutes</p>
      </div>

      <div className="recipeAudioAuthor">
        <UserPhoto
          classStylePhoto="recipeAudioAvatar"
          image={author?.avatar}
          name={author?.fullname}
        />
        <p className="recipeAudioAuthorLabel">Recipe by:</p>
        <p className="recipeAudioAuthorName">{author?.fullname}</p>
      </div>
    </div>
  )
}

export default RecipeAudioPlayer
